import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { Plus, Trash2, Edit2, X, CreditCard, Building2, Package, MapPin } from 'lucide-react';

const ChargebackTab = ({ caseId, assignedTo, user }) => {
  const [chargebackData, setChargebackData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [states, setStates] = useState([]);
  
  const [formData, setFormData] = useState({
    product: '',
    facilityId: '',
    facilityName: '',
    lastFourDigits: '',
    stateId: '',
    stateName: '',
    stateCode: ''
  });

  useEffect(() => {
    fetchChargebackData();
    fetchFacilities();
    fetchStates();
  }, [caseId]);

  const fetchChargebackData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/cases/${caseId}`);
      const caseData = response.data.data;
      // Store transactions array directly for easier access
      const transactions = caseData.chargebacksTransactions || [];
      setChargebackData({
        transactions: transactions,
        amountLost: caseData.chargebacksAmountLost || 0,
        amountRecovered: caseData.chargebacksAmountRecovered || 0
      });
    } catch (error) {
      console.error('Error fetching chargeback data:', error);
      toast.error('Failed to load chargeback data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await api.get('/facilities');
      setFacilities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      toast.error('Failed to load facilities');
    }
  };

  const fetchStates = async () => {
    try {
      const response = await api.get('/states');
      setStates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
      toast.error('Failed to load states');
    }
  };

  const canEdit = user?.role === 'Administrator' || 
                 user?.role === 'Supervisor' || 
                 (user?.role === 'Investigator' && (assignedTo?.id === user?.id || assignedTo?._id === user?._id));

  const handleFacilityChange = (e) => {
    const facilityId = e.target.value;
    const selectedFacility = facilities.find(f => f.id.toString() === facilityId.toString());
    
    setFormData({
      ...formData,
      facilityId: facilityId || '',
      facilityName: selectedFacility?.name || ''
    });
  };

  const handleStateChange = (e) => {
    const stateId = e.target.value;
    const selectedState = states.find(s => s.id.toString() === stateId.toString());
    
    setFormData({
      ...formData,
      stateId: stateId || '',
      stateName: selectedState?.name || '',
      stateCode: selectedState?.code || ''
    });
  };

  const handleLastFourDigitsChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData({
      ...formData,
      lastFourDigits: value
    });
  };

  const validateForm = () => {
    if (!formData.product) {
      toast.error('Product is required');
      return false;
    }
    if (!formData.facilityId) {
      toast.error('Facility is required');
      return false;
    }
    if (!formData.lastFourDigits || formData.lastFourDigits.length !== 4) {
      toast.error('Last four digits of the card must be exactly 4 digits');
      return false;
    }
    if (!formData.stateId) {
      toast.error('State is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const transactions = chargebackData?.transactions || [];
      const newTransaction = {
        product: formData.product,
        facilityId: formData.facilityId,
        facilityName: formData.facilityName,
        lastFourDigits: formData.lastFourDigits,
        stateId: formData.stateId,
        stateName: formData.stateName,
        stateCode: formData.stateCode,
        createdAt: new Date().toISOString()
      };

      let updatedTransactions;
      if (editingIndex !== null) {
        // Update existing transaction
        updatedTransactions = [...transactions];
        updatedTransactions[editingIndex] = {
          ...newTransaction,
          createdAt: transactions[editingIndex].createdAt // Preserve original creation date
        };
      } else {
        // Add new transaction
        updatedTransactions = [...transactions, newTransaction];
      }

      await api.put(`/cases/${caseId}`, {
        chargebacksTransactions: updatedTransactions
      });

      toast.success(editingIndex !== null ? 'Chargeback updated successfully' : 'Chargeback added successfully');
      setShowAddForm(false);
      setEditingIndex(null);
      resetForm();
      fetchChargebackData();
    } catch (error) {
      console.error('Error saving chargeback:', error);
      toast.error(error.response?.data?.message || 'Failed to save chargeback');
    }
  };

  const handleEdit = (index) => {
    const transaction = chargebackData.transactions[index];
    setFormData({
      product: transaction.product || '',
      facilityId: transaction.facilityId || '',
      facilityName: transaction.facilityName || '',
      lastFourDigits: transaction.lastFourDigits || '',
      stateId: transaction.stateId || '',
      stateName: transaction.stateName || '',
      stateCode: transaction.stateCode || ''
    });
    setEditingIndex(index);
    setShowAddForm(true);
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this chargeback entry?')) {
      return;
    }

    try {
      const transactions = [...chargebackData.transactions];
      transactions.splice(index, 1);

      await api.put(`/cases/${caseId}`, {
        chargebacksTransactions: transactions
      });

      toast.success('Chargeback deleted successfully');
      fetchChargebackData();
    } catch (error) {
      console.error('Error deleting chargeback:', error);
      toast.error(error.response?.data?.message || 'Failed to delete chargeback');
    }
  };

  const resetForm = () => {
    setFormData({
      product: '',
      facilityId: '',
      facilityName: '',
      lastFourDigits: '',
      stateId: '',
      stateName: '',
      stateCode: ''
    });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingIndex(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const transactions = chargebackData?.transactions || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Chargeback Information</h3>
        {canEdit && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Chargeback</span>
          </button>
        )}
      </div>

      {showAddForm && canEdit && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold text-gray-900">
              {editingIndex !== null ? 'Edit Chargeback' : 'Add New Chargeback'}
            </h4>
            <button
              onClick={handleCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Package className="w-4 h-4 inline mr-1" />
                  Product <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a product</option>
                  <option value="pin debit">Pin Debit</option>
                  <option value="prepaid collect(ppc)">Prepaid Collect (PPC)</option>
                  <option value="calling card">Calling Card</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Facility <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.facilityId}
                  onChange={handleFacilityChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a facility</option>
                  {facilities.map((facility) => (
                    <option key={facility.id} value={facility.id}>
                      {facility.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <CreditCard className="w-4 h-4 inline mr-1" />
                  Last Four Digits of Card <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.lastFourDigits}
                  onChange={handleLastFourDigitsChange}
                  maxLength={4}
                  placeholder="1234"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Enter the last 4 digits of the card</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" />
                  State <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.stateId}
                  onChange={handleStateChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select a state</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name} ({state.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingIndex !== null ? 'Update' : 'Add'} Chargeback
              </button>
            </div>
          </form>
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No chargeback information added yet</p>
          {canEdit && !showAddForm && (
            <p className="text-sm text-gray-400 mt-2">Click "Add Chargeback" to get started</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {transactions.map((transaction, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Product:</span>
                    <p className="mt-1 text-sm text-gray-900 capitalize">{transaction.product}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Facility:</span>
                    <p className="mt-1 text-sm text-gray-900">{transaction.facilityName}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Last Four Digits:</span>
                    <p className="mt-1 text-sm text-gray-900 font-mono">****{transaction.lastFourDigits}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">State:</span>
                    <p className="mt-1 text-sm text-gray-900">{transaction.stateName} ({transaction.stateCode})</p>
                  </div>
                  {transaction.createdAt && (
                    <div className="col-span-2">
                      <span className="text-sm font-medium text-gray-500">Added:</span>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(index)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChargebackTab;

